import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { EventCard, MarketCard, type EventGroupCard, type Market } from "../components/MarketCard";
import { FeaturedHero, pickFeatured } from "../components/FeaturedHero";
import { BrowseRail } from "../components/BrowseRail";
import { useAuth } from "../store/auth";

export function HomePage() {
  const { user } = useAuth();
  const markets = useQuery({
    queryKey: ["markets", "home"],
    queryFn: () => api<{ markets: Market[] }>("/markets?status=OPEN&sort=trending"),
  });
  const groups = useQuery({
    queryKey: ["groups"],
    queryFn: () => api<{ groups: EventGroupCard[] }>("/markets/groups"),
  });
  const list = markets.data?.markets ?? [];
  const hour = new Date().getHours();
  const hello = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const featured = pickFeatured(list);

  return (
    <>
      <FeaturedHero markets={featured} groups={groups.data?.groups ?? []} />
      <BrowseRail markets={list} />
      <div className="wrap">
        {user && <p className="welcome">{hello}{user.username ? `, ${user.username}` : ""}.</p>}
        {(groups.data?.groups ?? []).length > 0 && (
          <section className="section">
            <div className="section-h">
              <div>
                <span className="kicker">Events</span>
                <h2>Trade a whole story</h2>
              </div>
            </div>
            <div className="event-row">
              {(groups.data?.groups ?? []).slice(0, 6).map((g) => (
                <EventCard key={g.slug} group={g} />
              ))}
            </div>
          </section>
        )}
        <Section title="Trending now" kicker="Live book" to="/markets?sort=trending" items={list.slice(0, 6)} />
        <Section title="Closing soon" kicker="Deadlines" to="/markets?sort=closing" items={[...list].sort((a, b) => a.timeRemaining.localeCompare(b.timeRemaining)).slice(0, 3)} />
        <Section title="Most traded" kicker="Volume" to="/markets?sort=volume" items={[...list].sort((a, b) => Number(b.volume_total_micro) - Number(a.volume_total_micro)).slice(0, 3)} />

        <section className="section why-section">
          <div className="section-intro">
            <span className="kicker">Why Softwave</span>
            <h2>An exchange, not a sportsbook</h2>
            <p>Every fill has a counterparty. Collateral is reserved before a trade can exist. Fees are shown before you confirm.</p>
          </div>
          <div className="why-grid">
            <article className="card why-card">
              <span className="why-num">01</span>
              <h3>Peer-to-peer</h3>
              <p>When you buy Yes, another participant sells Yes or buys No. Softwave does not take the other side of ordinary flow.</p>
            </article>
            <article className="card why-card">
              <span className="why-num">02</span>
              <h3>Fully reserved</h3>
              <p>Cash is locked in a double-entry ledger first. Winning contracts redeem at $1 from that collateral.</p>
            </article>
            <article className="card why-card">
              <span className="why-num">03</span>
              <h3>Transparent fees</h3>
              <p>The standard taker fee is 0.50% of notional. There is no hidden house edge.</p>
            </article>
          </div>
        </section>
      </div>
    </>
  );
}

function Section({ title, kicker, to, items }: { title: string; kicker: string; to: string; items: Market[] }) {
  if (!items.length) return null;
  return (
    <section className="section">
      <div className="section-h">
        <div>
          <span className="kicker">{kicker}</span>
          <h2>{title}</h2>
        </div>
        <Link to={to}>View all</Link>
      </div>
      <div className="grid-3">
        {items.map((m) => <MarketCard key={m.id} market={m} />)}
      </div>
    </section>
  );
}
